import { FC, useState, useRef, useEffect, useCallback } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { SeturInputProps } from '@components/Input';
import { FormControlLabel } from '@components/ui/form/FormControl';

import {
  SeturInput,
  InputContainer,
  isStyledComponent,
  styled,
} from './styled';

type NativeOrAntdRef = HTMLInputElement | { input?: HTMLInputElement } | null;

const UseInputAPI = ({
  getInputAPI,
  applyValue,
  inputRef,
}: // TODO: Unknown typescript error (ISSUE: GSF-1611)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore:next-line
Pick<SeturInputProps, 'getInputAPI'> & {
  applyValue: (v: string) => void;
  inputRef: React.RefObject<NativeOrAntdRef> | undefined;
}) => {
  const reset = useCallback(() => {
    setTimeout(() => {
      applyValue('');
      const current = inputRef?.current;
      const el: HTMLInputElement | null = (() => {
        if (!current) return null;
        if (current instanceof HTMLInputElement) return current;
        if ('input' in current && current.input instanceof HTMLInputElement) {
          return current.input;
        }
        return null;
      })();
      if (el) {
        el.value = '';
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 0);
  }, [inputRef, applyValue]);
  useEffect(() => {
    getInputAPI?.({ applyValue, reset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const Input: FC<SeturInputProps> = ({
  className,
  hasError,
  label,
  width,
  icon = null,
  placeholder,
  suffix,
  value,
  onChange,
  disabled,
  InputComponent = SeturInput,
  inputRef,
  getInputAPI,
  $size = 'large',
  id = uuidv4(),
  ...rest
}) => {
  const [currentValue, setCurrentValue] = useState(value || rest.defaultValue);
  const defaultRefInputRef = useRef<NativeOrAntdRef>(null);
  const actualInputRef =
    (inputRef as React.RefObject<NativeOrAntdRef>) || defaultRefInputRef;
  const InputRef = useRef(
    isStyledComponent(InputComponent)
      ? InputComponent
      : styled(InputComponent)``
  );
  // Geniş tipli component referansı (suffix vb. ekstra props'u iletmek için)
  const ComponentWithExtraProps = InputComponent as unknown as FC<
    Record<string, unknown>
  >;

  return (
    <>
      {getInputAPI ? (
        <UseInputAPI
          getInputAPI={getInputAPI}
          applyValue={setCurrentValue}
          inputRef={actualInputRef}
        />
      ) : null}

      <InputContainer
        className={className}
        width={width}
        value={value || currentValue}
        disabled={disabled}
        InputComponent={InputRef.current}
      >
        {label ? (
          <FormControlLabel data-role='label' htmlFor={id}>
            {label}
          </FormControlLabel>
        ) : null}

        <ComponentWithExtraProps
          suffix={icon ?? suffix}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const {
              target: { value },
            } = e as unknown as { target: { value: string } };
            onChange?.(value);
            setCurrentValue(value);
          }}
          placeholder={placeholder}
          ref={actualInputRef as unknown as React.Ref<HTMLInputElement>}
          autoComplete='off'
          hasError={hasError as unknown as boolean}
          $size={$size as unknown as string}
          id={id}
          {...rest}
        />
      </InputContainer>
    </>
  );
};

export default Input;
export { InputContainer };
